<template>
  <BaseWidget
    title="Drop-in → return booking"
    :show-info-icon="true"
    info-tooltip="Number of drop-in visitors who made a return booking within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/3948766-how-do-i-enable-my-drop-in-widget-to-accept-bookings-from-my-website', text: 'Learn about Drop-ins' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :row-span="rowSpan"
    :col-span="colSpan"
    :date-range="dateRange"
    :past-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
        :show-table="!focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
      :class="{ 'focused': focused }"
    >
      <MetricValue
        :value="totalCount"
        :subtitle="dateRangeLabel"
        :has-data-table="accounts.length > 0"
      >
        <template #comparison>
          <ComparisonChip
            :change="changePercentage"
            :previous-value="previousCountFormatted"
            :previous-period-dates="previousTimestamps"
            :date-range-preset="dateRange"
            label="Drop-in return bookings"
          />
        </template>
        <template
          v-if="hasMore"
          #subtitle-suffix
        >
          <span
            class="focused-view-all-link"
            @click="openViewAllPanel"
          >View all</span>
        </template>
      </MetricValue>
      <div
        v-if="!focused && accounts.length > 0"
        class="accounts-list"
      >
        <div
          v-for="(account, index) in accounts"
          :key="account.account_id || index"
          class="account-row clickable"
          @click="handleAccountClickGuarded(account)"
        >
          <div class="user-image">
            <img
              v-if="account.avatar"
              :src="account.avatar"
              :alt="account.name"
            >
            <div
              v-else
              class="image-placeholder"
            >
              {{ getInitials(account.name) }}
            </div>
          </div>
          <span class="user-name">{{ account.name || 'Unknown' }}</span>
        </div>
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useDashboardKPIsData } from '../composables/useWidgetData.js'
import { DATE_RANGE_LABELS, getPreviousPeriodTimestamps } from '../utils/dateRanges.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { pushAdminSidePanel } from '../composables/useOptixPanel.js'

const props = defineProps({
  rowSpan: { type: Number, default: 1 },
  colSpan: { type: Number, default: 1 },
  editMode: { type: Boolean, default: false },
  props: { type: Object, default: () => ({}) },
  widgetId: { type: String, default: null },
  sectionId: { type: String, default: null },
  widgetType: { type: String, default: null },
  priority: { type: Number, default: null },
  focused: { type: Boolean, default: false }
})

const emit = defineEmits(['update:props', 'remove-widget'])

const dateRange = ref(props.props?.dateRange || 'last_30_days')

const dateRangeLabel = computed(() => DATE_RANGE_LABELS[dateRange.value] || 'Last 30 days')

const { data, loading, error, cleanup } = useDashboardKPIsData({
  variables: computed(() => ({ dateRange: dateRange.value })),
  priority: props.priority ?? 1
})

const previousTimestamps = computed(() => getPreviousPeriodTimestamps(dateRange.value))

const { data: previousData, loading: previousLoading, cleanup: previousCleanup } = useDashboardKPIsData({
  variables: computed(() => {
    const ts = previousTimestamps.value
    return { from_timestamp: ts.from_timestamp, to_timestamp: ts.to_timestamp }
  }),
  priority: props.priority ?? 1,
  totalsOnly: true
})

const totalCount = computed(() => {
  const total = data.value?.dashboardKPIs?.dropin_made_second_booking_accounts?.total
  if (total === null || total === undefined) return '0'
  return total.toString()
})

const previousCountFormatted = computed(() => {
  const total = previousData.value?.dashboardKPIs?.dropin_made_second_booking_accounts?.total
  if (total === null || total === undefined) return null
  return total.toString()
})

const changePercentage = computed(() => {
  if (loading.value || previousLoading.value) return null
  const current = data.value?.dashboardKPIs?.dropin_made_second_booking_accounts?.total ?? null
  const previous = previousData.value?.dashboardKPIs?.dropin_made_second_booking_accounts?.total ?? null
  if (current === null || previous === null) return null
  if (previous === 0 && current === 0) return 0
  if (previous === 0) return 100
  return Math.round(((current - previous) / previous) * 1000) / 10
})

const allAccountItems = computed(() => data.value?.dashboardKPIs?.dropin_made_second_booking_accounts?.data ?? [])

const accounts = computed(() => {
  return allAccountItems.value.slice(0, 3).map((item) => ({
    account_id: item.account_id,
    name: item.name || 'Unknown',
    avatar: getAvatarUrl(item.avatar?.url, item.name || item.account_id)
  }))
})

const hasMore = computed(() => allAccountItems.value.length > 3)

const { guard } = useTrialModeGuard()

const openViewAllPanel = guard(() => {
  pushAdminSidePanel('view-all-conversion-accounts', { conversionType: 'dropin-repeat-booking' })
})

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  return words.length >= 2 ? (words[0][0] + words[words.length - 1][0]).toUpperCase() : name.charAt(0).toUpperCase()
}

function getUrlParams() {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  if (document.referrer) {
    try {
      const referrerParams = new URLSearchParams(new URL(document.referrer).search)
      if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
    } catch (_e) {}
  }
  if ((!clientId || !canvasUUID) && window.parent && window.parent !== window) {
    try {
      const parentParams = new URLSearchParams(new URL(window.parent.location.href).search)
      if (!clientId) clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
    } catch (_e) {}
  }
  return { client_id: clientId, canvasUUID }
}

const handleAccountClickGuarded = guard((account) => {
  if (!account?.account_id) return
  const params = getUrlParams()
  const message = {
    command: 'ViewDetails',
    payload: {
      account_id: String(account.account_id),
      canvasUUID: params.canvasUUID || 'xxx'
    }
  }
  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('Failed to open account panel:', e)
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

watch(() => props.props?.dateRange, (v) => { if (v && v !== dateRange.value) dateRange.value = v })

onUnmounted(() => { cleanup(); previousCleanup() })
</script>

<style scoped>
.widget-content-wrapper { display: flex; flex-direction: column; height: 100%; min-height: 0; gap: 12px; }
.loading-state, .error-state { padding: 20px; text-align: center; color: rgba(0, 0, 0, 0.87); font-size: 14px; }
.error-state { color: var(--color-red); }
.accounts-list { flex: 1; min-height: 0; margin-top: 0; padding: 0; overflow: hidden; display: flex; flex-direction: column; }
.account-row { display: flex; align-items: center; gap: 12px; min-height: 64px; padding: 0; border-bottom: 1px solid rgba(0, 0, 0, 0.08); }
.account-row:first-child { border-top: 1px solid rgba(0, 0, 0, 0.08); }
.account-row:last-child { border-bottom: none; }
.account-row.clickable { cursor: pointer; }
.account-row.clickable:hover { background-color: rgba(0, 0, 0, 0.04); }
.user-image { width: 32px; height: 32px; border-radius: 50%; overflow: hidden; flex-shrink: 0; background: #f0f0f0; }
.user-image img { width: 100%; height: 100%; object-fit: cover; }
.image-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; font-weight: 600; font-size: 14px; }
.user-name { font-size: 13px; color: rgba(0, 0, 0, 0.87); font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.view-all-footer { display: flex; align-items: center; justify-content: flex-end; height: 50px; padding: 0 8px; border-top: 1px solid rgba(0, 0, 0, 0.08); }
.view-all-link { font-size: 13px; color: rgba(0, 0, 0, 0.87); font-weight: 400; cursor: pointer; transition: color 0.2s ease; }
.view-all-link:hover { color: #2563eb; }
.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  flex-shrink: 0;
}

.focused-view-all-link:hover {
  color: #2563eb;
}
</style>
